import React, { useState, useEffect, useMemo } from 'react';
import withAuth from '../../../auth';
import Pagination from '../../Pagination';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Imagesdata } from '../../../utils/imageData';
import { handleApiRequest } from '../../../utils/apiHandler';
import api from '../../../services/axios';
import { PICKUP_STATUS_COLORS } from '../../../constants/colors';
import PickupCardContent from './pickups/PickupCardContent';
import DraggablePickupCard from './pickups/DraggablePickupCard';
import PickupDropZone from './pickups/PickupDropZone';
import PickupFilters from './pickups/PickupFilters';
import { useManagerPickupEvents } from '../../../hooks/usePickupRealtime';

const PickupList = ({ pickups, setPickups, assignDriverToPickup }) => {
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    paid_status: 'paid',
  });

  useManagerPickupEvents({
    onCreated: () => fetchPickups(null, false),
    onUpdated: () => fetchPickups(null, false),
    onReconnect: () => fetchPickups(null, false),
  });

  useEffect(() => {
    fetchPickups();
  }, [currentPage]);

  const fetchPickups = async (newFilters = null, showLoading = true) => {
    if (showLoading) setLoading(true);
    const apiFilters = { ...(newFilters || filters) };

    delete apiFilters.paid_status;

    await handleApiRequest({
      requestFn: () =>
        api.post('/manager/pickups', {
          page: currentPage,
          ...apiFilters,
        }),

      onSuccess: ({ data, last_page }) => {
        setPickups(data);
        setTotalPages(last_page);
      },

      onError: (message) => {
        console.error('Failed to fetch deliveries:', message);
      },

      finallyFn: () => {
        if (showLoading) setLoading(false);
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (e, column) => {
    const newFilters = {
      ...filters,
      [column]: e.target.value,
    };

    const nonEmptyFilters = Object.keys(newFilters).reduce((acc, key) => {
      if (newFilters[key]) {
        acc[key] = newFilters[key];
      }
      return acc;
    }, {});

    setFilters(nonEmptyFilters);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1); // reset pagination when filter
    fetchPickups();
  };

  const handleDropdownChange = (e, column) => {
    const newFilters = {
      ...filters,
      [column]: e.target.value,
    };

    const nonEmptyFilters = Object.keys(newFilters).reduce((acc, key) => {
      if (newFilters[key]) {
        acc[key] = newFilters[key];
      }
      return acc;
    }, {});

    setFilters(nonEmptyFilters);
    setCurrentPage(1); // reset pagination when filter
    fetchPickups(nonEmptyFilters);
  };

  const availablePickups = useMemo(() => {
    return pickups.filter((pickup) => {
      if (pickup.delivery_status?.id === 6 || pickup.scheduled_pickup_time) {
        return false;
      }

      if (filters.paid_status === 'paid') {
        return pickup.waiting_for_driver_at !== null;
      }

      if (filters.paid_status === 'unpaid') {
        return pickup.waiting_for_driver_at === null;
      }

      return true;
    });
  }, [pickups, filters.paid_status]);

  return (
    <div className="card  p-2">
      <div className="d-flex justify-content-center align-items-center">
        <h3 className="fw-bold mb-1">List of deliveries</h3>
        <OverlayTrigger
          trigger="click"
          placement="top"
          container={document.body}
          rootClose
          overlay={
            <Popover
              className="popover custom-popover"
              style={{ zIndex: 1000 }}
            >
              <Popover.Body className="custom-popover-body">
                <div className="text-muted small">
                  Only booked deliveries are visible
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <img
            width={22}
            style={{
              cursor: 'pointer',
              marginLeft: '20px',
              position: 'absolute',
              top: '6px',
              right: '6px',
            }}
            src={Imagesdata('info')}
            alt="info"
          />
        </OverlayTrigger>
      </div>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '70vh' }}
        >
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <>
          {/* FILTERS */}
          <PickupFilters
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleDropdownChange={handleDropdownChange}
            handleSearchSubmit={handleSearchSubmit}
          />

          {/* PICKUPS */}
          <div
            style={{
              overflowY: 'auto',
              marginTop: '12px',
              paddingRight: '6px',
              height: '54vh',
            }}
          >
            <div className="d-flex flex-column gap-3">
              {availablePickups.map((pickup) => (
                <DraggablePickupCard key={pickup.id} pickup={pickup}>
                  <PickupDropZone pickup={pickup}>
                    <div
                      className="card border-0 driver-card"
                      style={{
                        backgroundColor:
                          PICKUP_STATUS_COLORS[pickup.delivery_status?.id] ||
                          '#fff',
                      }}
                    >
                      <PickupCardContent pickup={pickup} />
                    </div>
                  </PickupDropZone>
                </DraggablePickupCard>
              ))}
            </div>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && currentPage <= totalPages && (
            <div className="mt-2">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withAuth(PickupList);
